import { BrowserRouter, Route, Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { NavButtonFloating } from "./components/nav/nav";
import { Footer } from "./components/footer/footer";
import { IconButton } from "./components/button/icon-button";
import { Button } from "./components/button/button";
import { Icon } from "./components/icon/icon";
import { Dialog } from "./components/dialog/dialog";
import Home from "./pages/Home";
import Career from "./pages/Career";
import Contact from "./pages/Contact";
import Articles from "./pages/Articles";
import Article from "./pages/Article";
import Projects from "./pages/Projects";
import Photos from "./pages/Photos";
import PlurallProject from './pages/Plurall';
import Updates from "./pages/Updates";
import Games from "./pages/Games";
import QRCode from "./pages/QRCode";
import Tools from "./pages/Tools";
import Password from "./pages/Password";
import {
  I18nContext,
  LANGUAGE_STORAGE_KEY,
  Language,
  translations,
} from "./i18n";
import { PulseBackgroundProvider } from "./components/pulse-background";
import { PageTransitionRoutes } from "./components/page-transition";

const THEME_STORAGE_KEY = "theme";
const HUE_STORAGE_KEY = "hue-slider";

type ThemeMode = "light" | "dark";

const getInitialTheme = (): ThemeMode => {
  if (typeof window === "undefined") return "light";

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;

  return "light";
};

const getInitialLanguage = (): Language => {
  if (typeof window === "undefined") return "en";

  // Check query parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get("lang");
  if (langParam === "pt" || langParam === "en") {
    return langParam as Language;
  }

  // Fallback to localStorage
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored === "pt" ? "pt" : "en";
};

const shouldAskLanguage = (): boolean => {
  if (typeof window === "undefined") return false;
  return !window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
};

export default function App() {
  const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);
  const [themeOverride, setThemeOverride] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ||
      window.localStorage.getItem(THEME_STORAGE_KEY) === "light"
    );
  });
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const [showLanguageDialog, setShowLanguageDialog] =
    useState<boolean>(shouldAskLanguage);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(HUE_STORAGE_KEY);
    if (raw === null) return;
    const value = Number(raw);
    if (Number.isNaN(value)) return;
    document.documentElement.style.setProperty("--hue", String(Math.round(value)));
  }, []);

  // Handle language from query parameter
  useEffect(() => {
    if (typeof window === "undefined") return;

    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    if (langParam === "pt" || langParam === "en") {
      // Save to localStorage if set via query parameter
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, langParam);
      // Don't show language dialog if language was set via URL
      setShowLanguageDialog(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (!themeOverride) {
        setTheme(event.matches ? "dark" : "light");
      }
    };

    if (!themeOverride) {
      setTheme(mediaQuery.matches ? "dark" : "light");
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themeOverride]);

  const toggleTheme = () => {
    const nextTheme: ThemeMode = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    setThemeOverride(true);
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
  };

  const i18nValue = useMemo(
    () => ({
      language,
      setLanguage: (lang: Language) => {
        setLanguage(lang);
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        setShowLanguageDialog(false);
      },
      translations: translations[language],
    }),
    [language],
  );

  return (
    <I18nContext.Provider value={i18nValue}>
      <PulseBackgroundProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <NavButtonFloating
            label="Menu"
            items={[
              { label: translations[language].nav.home, route: "/" },
              { label: translations[language].nav.career, route: "/career" },
              {
                label: translations[language].nav.projects,
                route: "/projects",
              },
              {
                label: translations[language].nav.articles,
                route: "/articles",
              },
              { label: translations[language].nav.photos, route: "/photos" },
              { label: translations[language].nav.tools, route: "/tools" },
              { label: translations[language].nav.contact, route: "/contact" },
            ]}
          >
            <IconButton
              size="xl"
              ariaLabel={translations[language].nav.language}
              title={translations[language].nav.language}
              icon={
                <Icon
                  name="globe"
                  title={translations[language].nav.language}
                />
              }
              onClick={() => setShowLanguageDialog(true)}
            />
            <IconButton
              size="xl"
              active={theme === "dark"}
              ariaLabel={
                theme === "dark"
                  ? translations[language].nav.lightMode
                  : translations[language].nav.darkMode
              }
              title={
                theme === "dark"
                  ? translations[language].nav.lightMode
                  : translations[language].nav.darkMode
              }
              icon={
                <Icon
                  name={theme === "dark" ? "sun" : "moon"}
                  title={
                    theme === "dark"
                      ? translations[language].nav.lightMode
                      : translations[language].nav.darkMode
                  }
                />
              }
              onClick={toggleTheme}
            />
          </NavButtonFloating>
          <Dialog
            open={showLanguageDialog}
            onClose={() => setShowLanguageDialog(false)}
            title={translations[language].dialog.title}
            description={translations[language].dialog.description}
          >
            <Button
              size="lg"
              className="dialog-language-button"
              onClick={() => i18nValue.setLanguage("en")}
              ariaLabel={translations[language].dialog.english}
            >
              {/* <Icon name="globe" title={translations[language].dialog.english} /> */}
              {translations[language].dialog.english}
            </Button>
            <Button
              size="lg"
              className="dialog-language-button"
              onClick={() => i18nValue.setLanguage("pt")}
              ariaLabel={translations[language].dialog.portuguese}
            >
              {/* <Icon name="globe" title={translations[language].dialog.portuguese} /> */}
              {translations[language].dialog.portuguese}
            </Button>
          </Dialog>
          <div style={{ flex: 1 }}>
            <PageTransitionRoutes>
              <Route path="/" element={<Home />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:id" element={<Article />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/career" element={<Career />} />
              <Route path="/photos" element={<Photos />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/qrcode" element={<QRCode />} />
              <Route path="/tools/password" element={<Password />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              <Route path="/projects/plurall" element={<PlurallProject />} />
              <Route path="/contact" element={<Contact />} />
            </PageTransitionRoutes>
          </div>
        </div>
      </BrowserRouter>
      </PulseBackgroundProvider>
    </I18nContext.Provider>
  );
}
